import { Cell, CellState, TerrainGrid, cellNoise } from '@antification/core';
import { Camera, ScreenCoord } from './Camera';

/**
 * What the ground looks like.
 *
 * The grid decides what a cell *is* — natural, graded, curing, paved — and this
 * decides what colour that is and what grows on it. The two used to be one file
 * (ADR-008): `TerrainGrid` held a canvas, a palette and a cache, which put
 * `document.createElement` inside the module a settlement worker has to run.
 *
 * The cache is the reason this class has state at all. Twelve hundred diamonds
 * is too many to redraw at 60 fps for a picture that changes when someone paves
 * something, so the ground is drawn once onto an offscreen canvas and blitted
 * until the terrain, the zoom or the pan moves. `TerrainGrid.getTerrainVersion()`
 * is the signal: the same counter the traffic's route cache watches.
 *
 * Two things arrive per call rather than being held, and the split is the point:
 * `grid` is what the ground *is* and `camera` is where it is being looked at
 * from. The projection between them left `TerrainGrid` in ADR-009.
 */
export class TerrainRenderer {
  private cache: HTMLCanvasElement | null = null;

  /**
   * Set when the cache must be rebuilt for a reason the version cannot show.
   *
   * A loaded save restores the terrain version along with the terrain (see
   * `TerrainGrid.load`), so two different lots can carry the same number. The
   * client says so explicitly rather than trusting the counter to differ.
   */
  private dirty = true;

  private cachedVersion = -1;
  private cachedScale = 0;
  private cachedOffsetX = 0;
  private cachedOffsetY = 0;

  /** During a pan the cached image is translated rather than rebuilt. */
  private transforming = false;

  /**
   * @param createCanvas where the offscreen cache comes from. Defaults to the
   * document; a test hands in a recorder instead, which is how the drawing below
   * is checked without a browser (`tests/render.test.ts`).
   */
  constructor(private readonly createCanvas: () => HTMLCanvasElement =
    () => document.createElement('canvas')) {}

  /** Rebuild on the next frame regardless of what the terrain version says. */
  invalidate(): void {
    this.dirty = true;
  }

  /**
   * Set transforming state. During pan-only transforms (same scale),
   * the cached terrain image is translated by offset delta instead of rebuilding.
   */
  setTransforming(transforming: boolean): void {
    this.transforming = transforming;
  }

  // Render the grid (ground only, without tall vegetation)
  render(ctx: CanvasRenderingContext2D, camera: Camera, grid: TerrainGrid): void {
    const version = grid.getTerrainVersion();
    const scale = camera.getScale();
    const offset = camera.getOffset();

    // Check if cache needs to be updated (viewport changed or terrain modified)
    const terrainChanged = this.dirty || this.cachedVersion !== version;
    const scaleChanged = this.cachedScale !== scale;
    const offsetChanged = this.cachedOffsetX !== offset.x || this.cachedOffsetY !== offset.y;
    const needsUpdate = terrainChanged || scaleChanged || offsetChanged;

    // During transforms with only offset changes (no scale change, no dirty terrain),
    // translate the cached image instead of rebuilding for smoother performance
    if (this.transforming && !terrainChanged && !scaleChanged && offsetChanged && this.cache) {
      const dx = offset.x - this.cachedOffsetX;
      const dy = offset.y - this.cachedOffsetY;
      ctx.drawImage(this.cache, dx, dy);
      return;
    }

    if (needsUpdate) {
      this.updateCache(camera, grid, ctx.canvas.width, ctx.canvas.height);
    }

    // Draw cached terrain in one operation
    if (this.cache) {
      ctx.drawImage(this.cache, 0, 0);
    }
  }

  /**
   * Update the terrain cache. Called when terrain changes or viewport moves.
   */
  private updateCache(camera: Camera, grid: TerrainGrid, width: number, height: number): void {
    // Create or resize cache canvas
    if (!this.cache || this.cache.width !== width || this.cache.height !== height) {
      this.cache = this.createCanvas();
      this.cache.width = width;
      this.cache.height = height;
    }

    const cacheCtx = this.cache.getContext('2d')!;

    // Clear cache
    cacheCtx.clearRect(0, 0, width, height);

    // Render all cells to cache
    for (let y = 0; y < grid.getRows(); y++) {
      for (let x = 0; x < grid.getCols(); x++) {
        this.renderCell(cacheCtx, camera, grid, grid.getCell(x, y)!);
      }
    }

    // Update cache state
    this.dirty = false;
    this.cachedVersion = grid.getTerrainVersion();
    this.cachedScale = camera.getScale();
    this.cachedOffsetX = camera.getOffset().x;
    this.cachedOffsetY = camera.getOffset().y;
  }

  /**
   * Render a single vegetation item. Trees and bushes stand up out of the
   * ground, so they are drawn with the depth-sorted entities rather than into
   * the flat cache.
   */
  renderVegetation(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    grid: TerrainGrid,
    cell: Cell
  ): void {
    if (cell.state !== CellState.NATURAL) return;
    if (cell.vegetationType !== 'tree' && cell.vegetationType !== 'bush') return;

    const screen = camera.gridToScreen(cell.x, cell.y);
    this.drawVegetation(ctx, camera, cell, screen);
  }

  private renderCell(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    grid: TerrainGrid,
    cell: Cell
  ): void {
    const screen = camera.gridToScreen(cell.x, cell.y);
    const scale = camera.getScale();
    const w = camera.cellWidth * scale;
    const h = camera.cellHeight * scale;

    // Get color based on state
    const color = this.getCellColor(grid, cell);

    // Draw isometric diamond
    ctx.beginPath();
    ctx.moveTo(screen.x, screen.y - h / 2);      // Top
    ctx.lineTo(screen.x + w / 2, screen.y);      // Right
    ctx.lineTo(screen.x, screen.y + h / 2);      // Bottom
    ctx.lineTo(screen.x - w / 2, screen.y);      // Left
    ctx.closePath();

    ctx.fillStyle = color;
    ctx.fill();

    // Subtle grid lines
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
    ctx.lineWidth = 0.5;
    ctx.stroke();

    // Draw grass only (flat on the ground) - trees and bushes are depth-sorted separately
    if (cell.state === CellState.NATURAL && cell.vegetationType === 'grass') {
      this.drawVegetation(ctx, camera, cell, screen);
    }
  }

  private getCellColor(grid: TerrainGrid, cell: Cell): string {
    switch (cell.state) {
      case CellState.NATURAL:
        return this.getNaturalColor(cell);
      case CellState.SURVEYED:
        return '#a8d5a2'; // Lighter green (surveyed)
      case CellState.CLEARED:
        return '#c9b896'; // Dirt brown
      case CellState.GRADED:
        return '#b8a080'; // Flat dirt
      case CellState.POURED:
        return this.getConcreteColor(cell);
      case CellState.CURED:
        // Check if highway
        if (grid.isHighwayCell(cell.x, cell.y)) {
          return '#4a5568'; // Darker blue-gray for highway
        }
        return '#c0c0c0'; // Cured concrete gray
      case CellState.PAINTED:
        // Check if highway
        if (grid.isHighwayCell(cell.x, cell.y)) {
          return '#2d3748'; // Dark asphalt for highway
        }
        return '#a0a0a0'; // Darker asphalt gray
      case CellState.SEALED:
        // A facility's foundation. Colder and flatter than the mayor's concrete,
        // and never a highway however wide it is — nothing drives on it.
        return '#8f9296';
      default:
        return '#ff0000'; // Error color
    }
  }

  private getNaturalColor(cell: Cell): string {
    // Vary green based on position for natural look
    const baseGreen = 140 + Math.floor(cell.heightVariation * 20);
    return `rgb(100, ${baseGreen}, 80)`;
  }

  private getConcreteColor(cell: Cell): string {
    // Wet concrete is darker
    const wetness = 1 - cell.cureProgress;
    const gray = 160 + Math.floor(cell.cureProgress * 40);
    return `rgb(${gray}, ${gray}, ${gray + wetness * 10})`;
  }

  private drawVegetation(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    cell: Cell,
    screen: ScreenCoord
  ): void {
    const scale = camera.getScale();

    switch (cell.vegetationType) {
      case 'tree':
        // Simple tree: brown trunk + green circle
        ctx.fillStyle = '#5d4037';
        ctx.fillRect(screen.x - 2 * scale, screen.y - 15 * scale, 4 * scale, 12 * scale);
        ctx.beginPath();
        ctx.arc(screen.x, screen.y - 18 * scale, 8 * scale, 0, Math.PI * 2);
        ctx.fillStyle = '#2e7d32';
        ctx.fill();
        break;

      case 'bush':
        // Small bush
        ctx.beginPath();
        ctx.arc(screen.x, screen.y - 4 * scale, 5 * scale, 0, Math.PI * 2);
        ctx.fillStyle = '#558b2f';
        ctx.fill();
        break;

      case 'grass':
        // Just some grass tufts
        if (cellNoise(cell.x, cell.y, 5) > 0.7) {
          ctx.strokeStyle = '#7cb342';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(screen.x - 3 * scale, screen.y);
          ctx.lineTo(screen.x - 2 * scale, screen.y - 5 * scale);
          ctx.moveTo(screen.x, screen.y);
          ctx.lineTo(screen.x, screen.y - 6 * scale);
          ctx.moveTo(screen.x + 3 * scale, screen.y);
          ctx.lineTo(screen.x + 2 * scale, screen.y - 4 * scale);
          ctx.stroke();
        }
        break;
    }
  }
}
